import { Injectable, signal, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { NGXLogger } from 'ngx-logger';
import { ToastService } from '../shared/access/toast.service';
import { Observable, Subject, catchError, concat, map, of, switchMap, tap, throwError } from 'rxjs';
import { SignalRService } from '../shared/access/signalr.service';
import { DetailedOrder, FundingCheckDetails, Order } from './order.model';
import { isTimeDeposit, showErrorDialog, showSuccessSnackbar } from '../shared/utils/utils';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Comments,
  DeleteMultipleOrdersRequest,
  TradingCheckStatusRequest,
  UpdateApprovalStatusRequest,
  UpdateCheckStatusRequest,
  UpdateMultipleOrdersStatusRequest,
  UpdateOrderStatusRequest
} from '../shared/types/comments.model';
import { OrderToAddOrUpdate, OrderWithAllocationsToAddOrUpdate } from './order-with-allocations-to-add-or-update.model';
import {
  FlattenedOrderToAddOrUpdate,
  FlattenedOrderToAddOrUpdateWithRowNumber,
  InsertFlatOrderResponse
} from './flattened-order-to-add-or-update.model';
import { PdfOrder, PdfOrdersByBroker } from './pdf-order.model';
import { EmailSentEventDetails, OrderStatusUpdatedEventDetails, OrderUpdatedPayload } from './hub-events.model';
import { OrderSearchRequest } from './order-search-request.model';
import {
  PartialExecutionEdit,
  PartialExecutionQuantityUpdateRequest,
  PartialExecutionToAdd
} from './record-partial-execution/partial-execution.model';
import { PreTradeCheck } from '../shared/pretrade-check.model';
import { OrderWithExecutionInfo } from './bulk/bulk-execute-orders/order-with-execution-info.model';
import { BulkApproveExecutionsModel } from './bulk/bulk-approve-executions/order-with-execution-info.model';
import {
  PreTradeCheckRequestDto,
  PreTradeCheckTableRowDto
} from '../compliance/pre-trade-check/pre-trade-check-request.model';
import { BloombergTraderAccount } from '../shared/bloomberg-trader-account.model';
import { EnhancedPreTradeCheck } from './order-details/order-enhanced-pretrade-checks';
import { format } from 'date-fns';

function toApiDate(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : format(d, 'yyyy-MM-dd');
}

@Injectable({
  providedIn: 'root'
})
export class OrdersService {
  private http = inject(HttpClient);
  private logger = inject(NGXLogger);
  private toast = inject(ToastService);
  private signalRService = inject(SignalRService);

  private apiEndpoint = `${environment.apiEndpoint}/api/orders`;

  readonly orderUpdated: Observable<OrderUpdatedPayload>;
  readonly emailSent: Observable<EmailSentEventDetails>;
  private orderStatusUpdatedSubject = new Subject<OrderStatusUpdatedEventDetails>();
  readonly orderStatusUpdated = this.orderStatusUpdatedSubject.asObservable();

  private readonly _loadedOrders = signal<Order[] | null>(null);
  readonly loadedOrders = this._loadedOrders.asReadonly();

  setLoadedOrders(orders: Order[]): void {
    this._loadedOrders.set(orders);
  }

  /**
   * Runs the order-level funding check against a single sub-portfolio for an order that does not exist yet,
   * so the preparer sees whether the custodian being traded actually covers it. Deliberately not wrapped in
   * an error dialog — it supplements the form rather than gating it.
   */
  public previewSubPortfolioFunding$(request: {
    portfolioId: number;
    securityCode?: string;
    transactionType?: string;
    quantity: number;
    isNominalQuantity: boolean;
    unitPrice?: number;
    transactionDate?: string;
  }): Observable<FundingCheckDetails> {
    const params: Record<string, string> = {
      portfolioId: request.portfolioId.toString(),
      quantity: request.quantity.toString(),
      isNominalQuantity: request.isNominalQuantity.toString()
    };

    if (request.securityCode) params['securityCode'] = request.securityCode;
    if (request.transactionType) params['transactionType'] = request.transactionType;
    if (request.unitPrice) params['unitPrice'] = request.unitPrice.toString();
    if (request.transactionDate) params['transactionDate'] = request.transactionDate;

    return this.http.get<FundingCheckDetails>(`${this.apiEndpoint}/funding-preview`, { params }).pipe(
      catchError((error) => {
        this.logger.error(error);

        return throwError(() => error);
      })
    );
  }

  /**
   * Cash in the currency an FX order sells, on the top portfolio of the family being traded, net of other pending FX
   * orders and of unsettled credits FA has booked but not paid. A Sell states its nominal in the currency being sold;
   * a Purchase states it in the currency being bought, and the API prices it across.
   *
   * `excludeOrderId` keeps an order that already exists from counting as a claim against itself.
   */
  public previewFxFunding$(request: {
    portfolioId: number;
    sellCurrency?: string;
    buyCurrency?: string;
    transactionType?: string;
    nominal: number;
    transactionDate?: string;
    excludeOrderId?: number;
  }): Observable<FundingCheckDetails> {
    const params: Record<string, string> = {
      portfolioId: request.portfolioId.toString(),
      nominal: request.nominal.toString()
    };

    if (request.excludeOrderId) params['excludeOrderId'] = request.excludeOrderId.toString();

    if (request.sellCurrency) params['sellCurrency'] = request.sellCurrency;
    if (request.buyCurrency) params['buyCurrency'] = request.buyCurrency;
    if (request.transactionType) params['transactionType'] = request.transactionType;
    if (request.transactionDate) params['transactionDate'] = request.transactionDate;

    return this.http.get<FundingCheckDetails>(`${this.apiEndpoint}/fx/funding-preview`, { params }).pipe(
      catchError((error) => {
        this.logger.error(error);

        return throwError(() => error);
      })
    );
  }

  // Patches a single order into the shared cache in place, so consumers that only read
  // loadedOrders() (e.g. the order preview panel) see live updates without a full re-fetch.
  // No-op if the cache hasn't been populated yet — callers fall back to their own fetch in that case.
  upsertLoadedOrder(updated: Order): void {
    const current = this._loadedOrders();
    if (current === null) {
      return;
    }
    const idx = current.findIndex((o) => o.id === updated.id);
    this._loadedOrders.set(idx >= 0 ? current.map((o, i) => (i === idx ? updated : o)) : [...current, updated]);
  }

  constructor() {
    this.orderUpdated = this.signalRService.orderUpdated$;
    this.emailSent = this.signalRService.emailSent$;
    this.orderStatusUpdated = this.signalRService.orderStatusUpdated$;
  }

  listOrders$(
    showInactive: boolean | null,
    lucianosOrders: boolean | null,
    gtcGtdOrders: boolean | null,
    ids?: number[]
  ): Observable<Order[]> {
    const params: Record<string, string | number | boolean | readonly (string | number | boolean)[]> = {};

    if (showInactive) {
      params['showInactive'] = showInactive;
    }

    if (lucianosOrders) {
      params['lucianosOrders'] = lucianosOrders;
    }
    if (gtcGtdOrders) {
      params['gtcGtdOrders'] = gtcGtdOrders;
    }
    if (ids && ids.length > 0) {
      params['ids'] = ids;
    }
    return this.http
      .get<Order[]>(this.apiEndpoint, {
        params
      })
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);
          return throwError(() => error);
        })
      );
  }

  listEMSXOrders$(statusCodes: string[]): Observable<DetailedOrder[]> {
    const params: Record<string, string | number | boolean | readonly (string | number | boolean)[]> = {};

    if (statusCodes && statusCodes.length > 0) {
      params['statusCodes'] = statusCodes;
    }

    return this.http
      .get<DetailedOrder[]>(this.apiEndpoint + '/emsx-orders', {
        params
      })
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);
          return throwError(() => error);
        })
      );
  }

  sendTradableRequestEmail$(request: PreTradeCheckRequestDto): Observable<void> {
    return this.http.post<void>(`${this.apiEndpoint}/pre-trade-check/tradable`, request).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  sendRFQRequestEmail$(request: PreTradeCheckRequestDto): Observable<void> {
    return this.http.post<void>(`${this.apiEndpoint}/pre-trade-check/rfq`, request).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  searchOrders$(request: OrderSearchRequest): Observable<Order[]> {
    return this.http.post<Order[]>(`${this.apiEndpoint}/search`, request).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public getOrder$(id: number, silent = false): Observable<DetailedOrder> {
    return this.http.get<DetailedOrder>(`${this.apiEndpoint}/${id}`).pipe(
      catchError((error) => {
        this.logger.error(error);
        if (!silent) {
          showErrorDialog(error);
        }
        return throwError(() => error);
      })
    );
  }

  public getOrderForCloning$(id: number): Observable<OrderToAddOrUpdate> {
    return this.http.get<OrderToAddOrUpdate>(`${this.apiEndpoint}/${id}/for-cloning`).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public getOrderForCloningWithAllocation$(id: number): Observable<OrderWithAllocationsToAddOrUpdate> {
    return this.http
      .get<OrderWithAllocationsToAddOrUpdate>(`${this.apiEndpoint}/${id}/for-cloning-with-allocation`)
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);
          return throwError(() => error);
        })
      );
  }

  public getOrderForUpdate$(id: number): Observable<OrderWithAllocationsToAddOrUpdate> {
    return this.http.get<OrderWithAllocationsToAddOrUpdate>(`${this.apiEndpoint}/${id}/for-update`).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public getFxOrderForUpdate$(id: number): Observable<OrderWithAllocationsToAddOrUpdate> {
    return this.http.get<OrderWithAllocationsToAddOrUpdate>(`${this.apiEndpoint}/${id}/for-update/fx`).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public previewOrder$(id: number): Observable<string> {
    return this.http
      .get(`${this.apiEndpoint}/${id}/preview`, {
        responseType: 'text'
      })
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);
          return throwError(() => error);
        })
      );
  }

  public previewAggregatedOrder$(order: PdfOrder): Observable<string> {
    return this.http
      .post(`${this.apiEndpoint}/preview-aggregated`, order, {
        responseType: 'text'
      })
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);
          return throwError(() => error);
        })
      );
  }

  public updateOrderApprovalStatus$(id: number, request: UpdateApprovalStatusRequest): Observable<DetailedOrder> {
    return this.http.patch<DetailedOrder>(`${this.apiEndpoint}/${id}/approve`, request).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public updateOrderCheckStatus$(id: number, request: UpdateCheckStatusRequest): Observable<void> {
    return this.http.patch<void>(`${this.apiEndpoint}/${id}/check`, request).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public updateTradingOrderCheckStatus$(id: number, request: TradingCheckStatusRequest): Observable<void> {
    return this.http.patch<void>(`${this.apiEndpoint}/${id}/trading-check`, request).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public updateOrderStatus$(id: number, request: UpdateOrderStatusRequest): Observable<void> {
    return this.http.patch<void>(`${this.apiEndpoint}/${id}/update-status`, request).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public expireOrder$(id: number, request: UpdateOrderStatusRequest): Observable<void> {
    return this.http.patch<void>(`${this.apiEndpoint}/${id}/expire`, request).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public cancelOrder$(id: number, request: UpdateOrderStatusRequest): Observable<void> {
    return this.http.patch<void>(`${this.apiEndpoint}/${id}/cancel`, request).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public executeOrder$(order: OrderWithExecutionInfo): Observable<number> {
    return this.recordPartialExecution$(order.id, {
      comments: order.comments,
      isFinalExecution: true,
      quantity: order.useOrderInfo ? order.quantity : order.executedQuantity!,
      quantityType: order.useOrderInfo ? order.quantityType.code : order.executedQuantityType!,
      transactionRefNumber: order.transactionRef,
      executionPrice: order.executionPrice,
      executionBroker: order.executionBroker,
      executionStatus: order.executionStatus,
      workingQuantity: order.workingQuantity,
      tradeDate: toApiDate(order.tradeDate),
      tradeValueDate: toApiDate(order.tradeValueDate),
      ...(isTimeDeposit(order)
        ? {
            tdStartDate: toApiDate(order.executedTdStartDate),
            tdMaturityDate: toApiDate(order.executedTdMaturityDate),
            tdRate: order.executedTdRate ?? null
          }
        : {})
    }).pipe(map(() => order.id));
  }

  public recordPartialExecution$(id: number, partialExecution: PartialExecutionToAdd): Observable<void> {
    return this.http.patch<void>(`${this.apiEndpoint}/${id}/partial-execution`, partialExecution).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public bulkApprovePartialExecution$(orders: BulkApproveExecutionsModel): Observable<void> {
    return this.http.patch<void>(`${this.apiEndpoint}/partial-execution/approve/bulk`, orders).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public bulkRejectPartialExecution$(orders: BulkApproveExecutionsModel): Observable<void> {
    return this.http.patch<void>(`${this.apiEndpoint}/partial-execution/reject/bulk`, orders).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public approvePartialExecution$(
    orderId: number,
    executionId: string,
    comments: string,
    edits: PartialExecutionEdit | null = null
  ): Observable<void> {
    return this.http
      .patch<void>(`${this.apiEndpoint}/${orderId}/partial-execution/${executionId}/approve`, { comments, edits })
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);
          return throwError(() => error);
        })
      );
  }

  public rejectPartialExecution$(orderId: number, executionId: string, comments: string): Observable<void> {
    return this.http
      .patch<void>(`${this.apiEndpoint}/${orderId}/partial-execution/${executionId}/reject`, { comments })
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);
          return throwError(() => error);
        })
      );
  }

  public deletePartialExecution$(orderId: number, executionId: string, comments: string): Observable<void> {
    return this.http
      .delete<void>(`${this.apiEndpoint}/${orderId}/partial-execution/${executionId}?comments=${comments}`)
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);
          return throwError(() => error);
        })
      );
  }

  public updatePartialExecutionQuantity$(
    orderId: number,
    executionId: string,
    request: PartialExecutionQuantityUpdateRequest
  ): Observable<void> {
    return this.http.patch<void>(`${this.apiEndpoint}/${orderId}/partial-execution/${executionId}`, request).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public updateMultipleOrdersStatus$(request: UpdateMultipleOrdersStatusRequest): Observable<void> {
    const observables$ = request.orderIds.map((orderId) =>
      this.updateOrderStatus$(orderId, {
        orderId,
        newStatus: request.newStatus,
        comments: request.comments,
        cancelReason: request.cancelReason
      }).pipe(tap(() => this.orderStatusUpdatedSubject.next({ orderId, status: request.newStatus })))
    );

    return concat(...observables$);
  }

  public retryUploadOfOrder$(id: number): Observable<void> {
    return this.http.patch<void>(`${this.apiEndpoint}/${id}/retry-upload`, null).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public resendFax$(id: number): Observable<void> {
    return this.http.post<void>(`${this.apiEndpoint}/${id}/fax/resend`, null).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public retrySendOfOrder$(id: number): Observable<void> {
    return this.http.patch<void>(`${this.apiEndpoint}/${id}/retry-send`, null).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public insertOrder$(order: OrderWithAllocationsToAddOrUpdate): Observable<string> {
    return this.http
      .post(this.apiEndpoint, order, {
        responseType: 'text'
      })
      .pipe(
        tap((res) => showSuccessSnackbar(this.toast, res)),
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);

          return throwError(() => error);
        })
      );
  }

  public insertFlatOrder$(order: FlattenedOrderToAddOrUpdate, rowNumber: number): Observable<InsertFlatOrderResponse> {
    return this.http
      .post(`${this.apiEndpoint}/flat`, order, {
        responseType: 'text'
      })
      .pipe(
        map((res) => ({ success: true, orderId: Number.parseInt(res, 10), rowNumber })),
        catchError((error) => {
          this.logger.error(error);
          return of({ success: false, error, rowNumber });
        })
      );
  }

  public insertFlatFxOrder$(
    order: FlattenedOrderToAddOrUpdate,
    rowNumber: number
  ): Observable<InsertFlatOrderResponse> {
    return this.http
      .post(`${this.apiEndpoint}/fx/flat`, order, {
        responseType: 'text'
      })
      .pipe(
        map((res) => ({ success: true, orderId: Number.parseInt(res, 10), rowNumber })),
        catchError((error) => {
          this.logger.error(error);
          return of({ success: false, error, rowNumber });
        })
      );
  }

  public insertRestrictedFlatFxOrder$(
    order: FlattenedOrderToAddOrUpdate,
    rowNumber: number
  ): Observable<InsertFlatOrderResponse> {
    return this.http
      .post(`${this.apiEndpoint}/fx/flat/restricted`, order, {
        responseType: 'text'
      })
      .pipe(
        map((res) => ({ success: true, orderId: Number.parseInt(res, 10), rowNumber })),
        catchError((error) => {
          this.logger.error(error);
          return of({ success: false, error, rowNumber });
        })
      );
  }

  public updateOrder$(id: number, order: OrderWithAllocationsToAddOrUpdate): Observable<string> {
    return this.http.put<DetailedOrder>(`${this.apiEndpoint}/${id}`, order).pipe(
      tap(() => showSuccessSnackbar(this.toast, `Successfully updated order ${id}`)),
      map(() => `Successfully updated order ${id}`),
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public updateFxOrder$(id: number, order: OrderWithAllocationsToAddOrUpdate): Observable<string> {
    return this.http.put<DetailedOrder>(`${this.apiEndpoint}/${id}/fx`, order).pipe(
      tap(() => showSuccessSnackbar(this.toast, `Successfully updated order ${id}`)),
      map(() => `Successfully updated order ${id}`),
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public insertFxOrder$(order: OrderWithAllocationsToAddOrUpdate): Observable<string> {
    return this.http
      .post(`${this.apiEndpoint}/fx`, order, {
        responseType: 'text'
      })
      .pipe(
        tap((res) => showSuccessSnackbar(this.toast, res)),
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);

          return throwError(() => error);
        })
      );
  }

  public insertRestrictedFxOrder$(order: OrderWithAllocationsToAddOrUpdate): Observable<string> {
    return this.http
      .post(`${this.apiEndpoint}/fx/restricted`, order, {
        responseType: 'text'
      })
      .pipe(
        tap((res) => showSuccessSnackbar(this.toast, res)),
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);

          return throwError(() => error);
        })
      );
  }

  public executeOrderPreTradeCheck$(id: number): Observable<PreTradeCheck> {
    return this.http.post<PreTradeCheck>(`${this.apiEndpoint}/${id}/execute-pre-trade-check`, {}).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public parsePreTradeCheckFileFromDisk$(file: File): Observable<PreTradeCheckTableRowDto[]> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<{ path: string }>(`${this.apiEndpoint}/attachment/upload`, formData).pipe(
      switchMap(({ path }) => this.parsePreTradeCheckFile$(path)),
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public parsePreTradeCheckFile$(bulkOrdersFileTempPath: string): Observable<PreTradeCheckTableRowDto[]> {
    return this.http
      .post<PreTradeCheckTableRowDto[]>(`${this.apiEndpoint}/bulk/parse/pre-trade-check`, { bulkOrdersFileTempPath })
      .pipe(
        map((res) => res),
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);

          return throwError(() => error);
        })
      );
  }

  public parseBulkOrdersFile$(bulkOrdersFileTempPath: string): Observable<FlattenedOrderToAddOrUpdateWithRowNumber[]> {
    return this.http
      .post<FlattenedOrderToAddOrUpdate[]>(`${this.apiEndpoint}/bulk/parse`, { bulkOrdersFileTempPath })
      .pipe(
        map((res) =>
          res.map((order, idx) => ({
            ...order,
            accountName: order.accountName.trim(),
            portfolioInternalName: order.portfolioInternalName.trim(),
            rowNumber: idx + 1
          }))
        ),
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);

          return throwError(() => error);
        })
      );
  }

  public parseBulkFxOrdersFile$(
    bulkOrdersFileTempPath: string
  ): Observable<FlattenedOrderToAddOrUpdateWithRowNumber[]> {
    return this.http
      .post<FlattenedOrderToAddOrUpdate[]>(`${this.apiEndpoint}/fx/bulk/parse`, { bulkOrdersFileTempPath })
      .pipe(
        map((res) =>
          res.map((order, idx) => ({
            ...order,
            accountName: order.accountName.trim(),
            portfolioInternalName: order.portfolioInternalName.trim(),
            rowNumber: idx + 1
          }))
        ),
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);

          return throwError(() => error);
        })
      );
  }

  public parseBulkSimpleFxOrdersFile$(
    bulkOrdersFileTempPath: string
  ): Observable<FlattenedOrderToAddOrUpdateWithRowNumber[]> {
    return this.http
      .post<FlattenedOrderToAddOrUpdate[]>(`${this.apiEndpoint}/fx/simple/bulk/parse`, { bulkOrdersFileTempPath })
      .pipe(
        map((res) =>
          res.map((order, idx) => ({
            ...order,
            accountName: order.accountName.trim(),
            portfolioInternalName: order.portfolioInternalName.trim(),
            rowNumber: idx + 1
          }))
        ),
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);

          return throwError(() => error);
        })
      );
  }

  public parseBulkFxOrdersFileFromDisk$(file: File): Observable<FlattenedOrderToAddOrUpdateWithRowNumber[]> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<{ path: string }>(`${this.apiEndpoint}/attachment/upload`, formData).pipe(
      switchMap(({ path }) => this.parseBulkFxOrdersFile$(path)),
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public parseBulkSimpleFxOrdersFileFromDisk$(file: File): Observable<FlattenedOrderToAddOrUpdateWithRowNumber[]> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<{ path: string }>(`${this.apiEndpoint}/attachment/upload`, formData).pipe(
      switchMap(({ path }) => this.parseBulkSimpleFxOrdersFile$(path)),
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public getBulkOrdersFileTemplate$(): Observable<Blob> {
    return this.http.get(`${this.apiEndpoint}/bulk/template`, { responseType: 'blob' }).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public getBulkPreTradeCheckFileTemplate$(): Observable<Blob> {
    return this.http.get(`${this.apiEndpoint}/bulk/template/pre-trade-check`, { responseType: 'blob' }).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public getBulkFxOrdersFileTemplate$(): Observable<Blob> {
    return this.http.get(`${this.apiEndpoint}/fx/bulk/template`, { responseType: 'blob' }).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public getBulkSimpleFxOrdersFileTemplate$(): Observable<Blob> {
    return this.http.get(`${this.apiEndpoint}/fx/simple/bulk/template`, { responseType: 'blob' }).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  /**
   * Sets the desk follow-up action on an order, or clears it back to unset when passed null.
   * The API pushes an `orderUpdated` hub event on success, so callers don't need to reload.
   */
  public updateFollowUpAction$(id: number, followUpAction: string | null): Observable<void> {
    return this.http.patch<void>(`${this.apiEndpoint}/${id}/follow-up-action`, { followUpAction }).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  /** Sets the desk-only internal note, or clears it when passed null. Raises an `orderUpdated` hub event. */
  public updateAdditionalInternalInformation$(
    id: number,
    additionalInternalInformation: string | null
  ): Observable<void> {
    return this.http
      .patch<void>(`${this.apiEndpoint}/${id}/additional-internal-information`, { additionalInternalInformation })
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);

          return throwError(() => error);
        })
      );
  }

  public sendEmailToBroker$(id: number): Observable<void> {
    return this.http.post<void>(`${this.apiEndpoint}/${id}/send`, null).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public sendOrdertoFa$(id: number): Observable<void> {
    return this.http.post<void>(`${this.apiEndpoint}/${id}/fa-send`, null).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public sendMultipleOrdersEmailToBroker$(ordersByBrokers: PdfOrdersByBroker[]): Observable<object> {
    return this.http.post(`${this.apiEndpoint}/send`, ordersByBrokers).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  createBulkOrderOnFA$(orders: Order[]): Observable<object> {
    return this.http
      .post(
        `${this.apiEndpoint}/for-bulk/fa`,
        orders.map((order) => order.id)
      )
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);

          return throwError(() => error);
        })
      );
  }

  public exportFxOrderToExcel$(id: number): Observable<Blob> {
    return this.http.get(`${this.apiEndpoint}/${id}/export/fx/excel`, { responseType: 'blob' }).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public exportFxOrderToCSV$(id: number): Observable<Blob> {
    return this.http.get(`${this.apiEndpoint}/${id}/export/fx/csv`, { responseType: 'blob' }).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public exportMultipleFxOrdersToExcel$(ids: number[], broker: string): Observable<Blob> {
    return this.http
      .post(`${this.apiEndpoint}/export/multiple/fx/excel`, { ids, broker }, { responseType: 'blob' })
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);

          return throwError(() => error);
        })
      );
  }

  public exportMultipleFxOrdersToCSV$(ids: number[], broker: string): Observable<Blob> {
    return this.http.post(`${this.apiEndpoint}/export/multiple/fx/csv`, { ids, broker }, { responseType: 'blob' }).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public deleteOrder$(id: number, comments: Comments): Observable<void | object> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: comments
    };

    return this.http.delete(`${this.apiEndpoint}/${id}`, options).pipe(
      tap(() => showSuccessSnackbar(this.toast, `Order ${id} was deleted successfully.`)),
      catchError((error) => {
        this.logger.error(error);
        return throwError(() => error);
      })
    );
  }

  public deleteMultipleOrders$(request: DeleteMultipleOrdersRequest): Observable<void | object> {
    const ids = request.orderIds;
    const comments = request.comments;

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: { ids, comments: { comments } }
    };

    return this.http.delete(`${this.apiEndpoint}`, options).pipe(
      tap(() => showSuccessSnackbar(this.toast, `Orders were deleted successfully.`)),
      catchError((error) => {
        this.logger.error(error);
        return throwError(() => error);
      })
    );
  }

  public listBloombergTraderAccounts$(): Observable<BloombergTraderAccount[]> | null {
    return this.http.get<BloombergTraderAccount[]>(`${this.apiEndpoint}/bloomberg-trader-accounts`).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);

        return throwError(() => error);
      })
    );
  }

  public insertEnhancedPreTradeCheck$(id: number, check: EnhancedPreTradeCheck): Observable<void> {
    return this.http.post<void>(`${this.apiEndpoint}/${id}/enhanced-pre-trade-check`, { ...check }).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  public setGtcGtdreminderDateForOrder(id: number, gtcGtdreminderDate: Date): Observable<void> {
    return this.http
      .patch<void>(`${this.apiEndpoint}/gtc-gtd-reminder?id=${id}&date=${gtcGtdreminderDate.toISOString()}`, null)
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          showErrorDialog(error);
          return throwError(() => error);
        })
      );
  }

  generateEodTradeSummaryEmail$(): Observable<void> {
    return this.http.get<void>(`${this.apiEndpoint}/eod-trade-summary`, {}).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  generateGtcGtdSummaryEmail$(): Observable<void> {
    return this.http.get<void>(`${this.apiEndpoint}/gtc-gtd-trade-summary`, {}).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }

  generateMaturingTimeDepositsEmail$(): Observable<void> {
    return this.http.get<void>(`${this.apiEndpoint}/maturing-time-deposits-email`, {}).pipe(
      catchError((error) => {
        this.logger.error(error);
        showErrorDialog(error);
        return throwError(() => error);
      })
    );
  }
}
